import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { RequestTypesCard } from './RequestTypesCard';
import { IoCubeOutline } from 'react-icons/io5';
import { UseTaskReturn } from 'vig-ui-library';
import { RequestTypeWithTagsDto } from '../RequestForm/Schemas/RequestTypeWithTagsDto';
import { RequestTagData } from '../RequestForm/Schemas/RequestTagData';
import { RequestTypeDto } from '../RequestForm/Schemas/RequestTypeDto';
import { RequestStepList, RequestStepListProps } from './RequestStepList';

export type RequestTypeListProps = {
    requestTypes: RequestTypeWithTagsDto[];
    getAllRequestTypesTask: UseTaskReturn<[], void>;
    onRequestTypeSelected: (requestType: string) => void;
    onParentTagSelected: (parentTag: string | null) => void;
    requestStepListSx?: RequestStepListProps["sx"];
    availableWidth?: number;
}

export default function RequestTypeList({
    requestTypes,
    getAllRequestTypesTask,
    onRequestTypeSelected,
    onParentTagSelected,
    requestStepListSx,
    availableWidth,
}: RequestTypeListProps) {

    function requestChanged(requestToSelect: RequestTagData | RequestTypeDto | null, type: "tag" | "row" | "request") {
        if (type === 'tag') {
            setParentTag(requestToSelect);
        } else {
            if (!requestToSelect) return;
            let id = requestToSelect.id;
            if (typeof id === 'string') id = parseInt(id);
            onRequestTypeSelected(id.toString());
        }
    }

    function setParentTag(parentTag: any) {
        if (parentTag && parentTag.id) {
            onParentTagSelected(String(parentTag.id));

        } else {
            onParentTagSelected(null);
        }
    }

    const shouldShowSkeletons = getAllRequestTypesTask.isRunning && (getAllRequestTypesTask.isInitialCall || !requestTypes?.length);

    return (
        <Box>
            {shouldShowSkeletons && (
                <RequestCardsSkeleton requestStepListSx={requestStepListSx} availableWidth={availableWidth} />
            )}

            {!!requestTypes?.length && (
                <RequestCards
                    requestChanged={requestChanged}
                    requestTypes={requestTypes}
                    requestStepListSx={requestStepListSx}
                    availableWidth={availableWidth}
                />
            )}

            {!requestTypes?.length && !getAllRequestTypesTask.isRunning && <RequestTypesNotFound />}
        </Box>
    );
}

type RequestsCardsProps = {
    requestTypes: RequestTypeWithTagsDto[];
    requestChanged: (requestToSelect: RequestTagData | RequestTypeDto | null, type: "tag" | "row" | "request") => void;
    requestStepListSx?: RequestStepListProps["sx"];
    availableWidth?: number;
}

const RequestCards = ({ requestChanged, requestTypes, requestStepListSx, availableWidth }: RequestsCardsProps) => {
    return (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
            <RequestStepList sx={requestStepListSx} availableWidth={availableWidth}>
                {requestTypes.map((rt: RequestTypeWithTagsDto) => {
                    const requestType = rt.requestType || rt.requestTag;

                    if (!requestType) return <></>

                    return (
                        <Box key={requestType?.id}>
                            <RequestTypesCard
                                requestType={requestType}
                                onClick={() => {
                                    if (rt.requestType) requestChanged(rt.requestType, 'request');
                                    if (rt.requestTag) requestChanged(rt.requestTag, 'tag');
                                }}
                                isTag={!!rt.requestTag}
                            />
                        </Box>
                    );
                })}
            </RequestStepList>
        </Box>
    );
}

type RequestCardsSkeletonProps = {
    requestStepListSx?: RequestStepListProps["sx"];
    availableWidth?: number;
}

const RequestCardsSkeleton = ({ requestStepListSx, availableWidth }: RequestCardsSkeletonProps) => {
    const theme = useTheme();

    return (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
            <RequestStepList sx={requestStepListSx} availableWidth={availableWidth}>
                {Array.from({ length: 3 }).map((_, index) => (
                    <Box key={`request-card-skeleton-${index}`}>
                        <Skeleton
                            variant="rounded"
                            height={178}
                            animation="wave"
                            sx={{
                                width: "100%",
                                maxWidth: 424,
                                borderRadius: "7px",
                                backgroundColor: theme.palette.grey[100],
                                [theme.breakpoints.down(424 * 2 + 32)]: {
                                    maxWidth: "none",
                                },
                                "&::after": {
                                    background: `linear-gradient(90deg, transparent, ${theme.palette.common.white}, transparent)`,
                                },
                            }}
                        />
                    </Box>
                ))}
            </RequestStepList>
        </Box>
    );
}

const RequestTypesNotFound = () => {
    const theme = useTheme();

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
            gap: 2
        }}>
            <IoCubeOutline size={32} color={theme.palette.grey[600]} />

            <Typography variant='subtitle2' color={theme.palette.text.secondary}>
                <i>Nevieme nájsť žiadne žiadosti</i>
            </Typography>
        </Box>
    );
}